import type { AttackSkill } from '../../../battle/types';
import { playSfx } from '../../../render/utils/audio';
import type { SkillBundle } from '../../types';
import { Cannonball } from './Cannonball';

const ROOT = '/assets/textures/characters/sparky_bot/skills/cannon';

// Frame in the cannon sheet at which the muzzle fires (smoke wisp visible).
// Default - overridable per character via timings.skills.cannon.impactFrame.
const FIRE_FRAME = 9;
// Spawn / impact heights are FRACTIONS of bodyHeight measured from the feet.
// 0.5 = mid-body, 0.7 = chest area, 1.0 = top of head. Increase to raise.
const MUZZLE_HEIGHT_FRAC = 0.6;
const IMPACT_HEIGHT_FRAC = 0.6;
// Forward offset from attacker's center, as fraction of bodyWidth.
const MUZZLE_FORWARD_FRAC = 0.4;

const ENGINE: AttackSkill = {
  kind: 'attack',
  id: 'cannon',
  name: 'Cannon',
  triggerChance: 0.3,         // 30% base; tunable, +luck*LUCK_COEF at runtime
  damageMultiplier: 3,
  pierceBlock: false,
  animationId: 'cannon',
  launches: false, // no knock-up - defender plays plain pain on a cannon hit
};

export const CANNON: SkillBundle = {
  engine: ENGINE,
  assets: {
    sheets: {
      cannon: { url: `${ROOT}/cannon_v2.png`, cols: 4, rows: 4 },
    },
    textures: {
      cannonball: `${ROOT}/cannonball.png`,
      logIcon: `${ROOT}/ui-log-icon.png`,
    },
    sfx: {
      shot: `${ROOT}/cannonshot.mp3`,
      impact: `${ROOT}/impact.mp3`, // TODO Change this SFX
      ricochet: `${ROOT}/ricochet.mp3`,
    },
  },
  async playAttack(ctx) {
    const { attacker, defender, scene, assets } = ctx;
    const cannonFrames = assets.sheets.cannon;
    const cannonballTex = assets.textures.cannonball;
    if (!cannonFrames || !cannonballTex) return;

    // Per-character overrides (speed / offset / fire frame) fall back to the
    // skill's built-in defaults.
    const t = attacker.timings.skills?.[ENGINE.id];
    const fireFrame = t?.impactFrame ?? FIRE_FRAME;

    // Cannon anim plays in place - no lunge. Recovery frames keep playing in
    // bg after the projectile leaves; schedule idle snap so the attacker
    // doesn't freeze on the last frame.
    attacker.playFrames(cannonFrames, {
      loop: false,
      speed: t?.speed,
      offsetX: t?.offsetX,
      offsetY: t?.offsetY,
      scaleMultiplier: t?.scale,
    });
    attacker.scheduleIdleAfterCurrentAnim();

    // Wait until the muzzle fires before spawning the projectile.
    await attacker.awaitFrame(fireFrame);

    // Boom. Master volume applied via playSfx helper.
    const shotSfx = assets.sfx.shot;
    if (shotSfx) playSfx(shotSfx);

    // Spawn at the muzzle (offset from attacker center, height from feet).
    const startX = attacker.x + attacker.bodyWidth * MUZZLE_FORWARD_FRAC * attacker.facing;
    const startY = attacker.y - attacker.bodyHeight * MUZZLE_HEIGHT_FRAC;
    const targetX = defender.x;
    const targetY = defender.y - defender.bodyHeight * IMPACT_HEIGHT_FRAC;

    const blocked = ctx.defenderReaction === 'block';
    // No passthrough: ball disposes itself at impact. The "ricochet into wall"
    // visual on block is owned by the arena (via playArenaEvent below) so the
    // embed appears to fly out of the impact point continuously, not after a
    // separate off-screen detour.
    const ball = new Cannonball({
      texture: cannonballTex,
      startX,
      startY,
      targetX,
      targetY,
    });
    scene.spawnEffect(ball);
    // Block until the projectile lands - renderer's damage event fires next
    // and applies the actual hit on the defender at this exact moment.
    await ball.flyAndImpact();

    // Impact vs ricochet are mutually exclusive - choose based on outcome.
    // Direct hit: heavy thud on defender. Blocked: metallic ping off armor.
    const hitSfx = blocked ? assets.sfx.ricochet : assets.sfx.impact;
    if (hitSfx) playSfx(hitSfx);

    // On block: hand off to the arena's embed system synchronously so the
    // ball lodges in the wall as part of the same continuous motion. The
    // engine still emits an arena_attach event a few steps later; the arena's
    // playArenaEvent is idempotent so that second call no-ops.
    if (blocked) {
      const arena = ctx.scene.getArena();
      if (arena?.bundle.playArenaEvent) {
        await arena.bundle.playArenaEvent(
          {
            type: 'arena_attach',
            side: ctx.defenderActor,
            objectId: 'cannonball',
            sourceSkillId: 'cannon',
            fromPosition: { x: targetX, y: targetY },
          },
          { scene: ctx.scene, sheets: arena.sheets, textures: arena.textures },
        );
      }
    }
  },
};
